import { Component, Input, ViewChild } from '@angular/core';
import { NgbActiveModal, NgbDatepicker, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { MovementService } from '../../services/movement.service';
import { AwsService } from '../../services/aws.service';
import { PaymentMethodService } from '../../services/payment-method.service';
import { PaymentMethod } from '../../models/payment-method.model';
import moment from 'moment';

@Component({
  selector: 'app-movement-modal',
  templateUrl: './movement-modal.component.html',
  styleUrl: './movement-modal.component.scss'
})
export class MovementModalComponent {

  @Input() idBranch: string = '';
  @Input() movement?: any;

  @ViewChild('dp') dp?: NgbDatepicker;

  public model?: NgbDateStruct;
  public date?: { year: number; month: number };

  public movementTypes = [
    { value: true, label: 'Ingreso' },
    { value: false, label: 'Egreso' }
  ];

  public statusOptions = [
    { value: false, label: 'Pendiente' },
    { value: true, label: 'Completado' }
  ];

  public paymentMethods: PaymentMethod[] = [];

  // Form fields
  public description: string = '';
  public notes: string = '';
  public movementType: boolean | null = null;
  public status: boolean | null = null;
  public amount: number | null = null;
  public idPaymentMethod: string | null = null;
  public bankReference: string = '';
  public hasInvoice: boolean = false;

  // Files
  public ticketFileName: string = '';
  public invoicePdfFileName: string = '';
  public invoiceXmlFileName: string = '';
  public ticketFile: File | null = null;
  public invoicePdfFile: File | null = null;
  public invoiceXmlFile: File | null = null;

  public submitted: boolean = false;
  public loading: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    public awsService: AwsService,
    public movementService: MovementService,
    private paymentMethodService: PaymentMethodService,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  get showBankReference(): boolean {
    if (!this.idPaymentMethod) return false;
    const pm = this.paymentMethods.find(p => p.idPaymentMethod === this.idPaymentMethod);
    return pm ? pm.paymentMethod.toLowerCase() !== 'efectivo' : true;
  }

  // region loadData
  async loadData() {
    this.loading = true;
    this.paymentMethods = await this.paymentMethodService.getAll();

    if (this.movement) {
      this.description = this.movement.description || '';
      this.notes = this.movement.notes || '';
      this.movementType = this.movement.movementType;
      this.status = this.movement.status;
      this.amount = parseFloat(this.movement.amount);
      this.idPaymentMethod = this.movement.idPaymentMethod || null;
      this.bankReference = this.movement.bankReference || '';
      this.hasInvoice = this.movement.invoice || false;

      if (this.movement.ticket?.urlImage) {
        this.ticketFileName = this.movement.ticket.urlImage.split('/').pop() || 'ticket';
      }
      if (this.movement.invoicePdf?.urlImage) {
        this.invoicePdfFileName = this.movement.invoicePdf.urlImage.split('/').pop() || 'factura.pdf';
      }
      if (this.movement.invoiceXml?.urlImage) {
        this.invoiceXmlFileName = this.movement.invoiceXml.urlImage.split('/').pop() || 'factura.xml';
      }

      if (this.movement.createdAt) {
        const d = moment(this.movement.createdAt).add(-6, 'hours');
        this.model = { year: d.year(), month: d.month() + 1, day: parseInt(d.format('D')) };
      }
    } else {
      const today = new Date();
      this.model = { year: today.getFullYear(), month: today.getMonth() + 1, day: today.getDate() };
    }

    this.loading = false;
  }

  // region onSubmit
  async onSubmit() {
    this.submitted = true;
    if (!this.description || this.movementType === null || this.status === null || !this.amount) return;

    this.loading = true;

    if (this.movement) {
      await this.updateMovement();
    } else {
      await this.createMovement();
    }

    this.loading = false;
  }

  // region createMovement
  private async createMovement() {
    const body: any = {
      description: this.description,
      notes: this.notes,
      amount: String(this.amount),
      movementType: this.movementType,
      idPaymentMethod: this.idPaymentMethod || null,
      bankReference: this.bankReference,
      invoice: this.hasInvoice,
      status: this.status,
    };

    const created = await this.movementService.create(body, this.idBranch);
    if (created) {
      if (this.ticketFile || this.invoicePdfFile || this.invoiceXmlFile) {
        await this.uploadFiles(created.idMovement, this.idBranch);
      }
      this.activeModal.close();
    }
  }

  // region updateMovement
  private async updateMovement() {
    const payload: any = {
      idMovement: this.movement.idMovement,
      idBranch: this.movement.idBranch,
      description: this.description,
      notes: this.notes,
      amount: String(this.amount),
      movementType: this.movementType,
      idPaymentMethod: this.idPaymentMethod || null,
      bankReference: this.bankReference,
      invoice: this.hasInvoice,
      status: this.status,
    };

    if (this.model) {
      payload.createdAt = `${this.model.year}-${String(this.model.month).padStart(2, '0')}-${String(this.model.day).padStart(2, '0')}T13:00:00`;
    }

    const success = await this.movementService.update(payload);
    if (success) {
      if (this.ticketFile || this.invoicePdfFile || this.invoiceXmlFile) {
        await this.uploadFiles(this.movement.idMovement, this.movement.idBranch);
      }
      this.activeModal.close();
    }
  }

  // region onFileChange
  onFileChange(event: any, fileType: string) {
    const file = event.target.files[0];
    if (!file) return;

    switch (fileType) {
      case 'ticket':
        this.ticketFile = file; this.ticketFileName = file.name; break;
      case 'invoicePdf':
        this.invoicePdfFile = file; this.invoicePdfFileName = file.name; break;
      case 'invoiceXml':
        this.invoiceXmlFile = file; this.invoiceXmlFileName = file.name; break;
    }
  }

  // region uploadFiles
  async uploadFiles(idMovement: string, idBranch: string) {
    if (this.ticketFile) {
      const presign = await this.movementService.uploadTicket(idMovement, idBranch, this.ticketFile.type);
      if (presign?.uploadUrl) {
        await this.awsService.uploadFile(presign.uploadUrl, this.ticketFile, this.ticketFile.type);
      }
    }

    if (this.invoicePdfFile) {
      const presign = await this.movementService.uploadInvoicePdf(idMovement, idBranch, this.invoicePdfFile.type);
      if (presign?.uploadUrl) {
        await this.awsService.uploadFile(presign.uploadUrl, this.invoicePdfFile, this.invoicePdfFile.type);
      }
    }

    if (this.invoiceXmlFile) {
      const presign = await this.movementService.uploadInvoiceXml(idMovement, idBranch, this.invoiceXmlFile.type);
      if (presign?.uploadUrl) {
        await this.awsService.uploadFile(presign.uploadUrl, this.invoiceXmlFile, this.invoiceXmlFile.type);
      }
    }
  }

}
